/**
 * MTD ITSA Compliance Registry — Single Source of Truth
 *
 * All dated MTD facts, thresholds, deadlines, and positioning statements
 * used across the ZapExpenses frontend MUST be sourced from this file.
 *
 * Status: AWAITING_ACCOUNTANT_REVIEW
 * Content owner: Founder
 * Sources accessed: 2026-08-20; re-checked against live GOV.UK 2026-09-04
 * Hedge wording for penalties, digital links, photos, positioning and retention
 * applied 2026-09-04 from a GOV.UK-cited review. Still not a practising-accountant sign-off.
 * Next scheduled review: 2026-11-20 (quarterly)
 */

// ─── Review metadata ────────────────────────────────────────────────────────
export const CONTENT_REVIEWED_ISO = '2026-09-04T21:00:00Z';
export const REVIEW_STATUS = 'AWAITING_ACCOUNTANT_REVIEW';
export const NEXT_REVIEW_DATE = '2026-11-20';
export const CONTENT_OWNER = 'Founder';

// ─── MTD thresholds & phases ─────────────────────────────────────────────────
export const MTD_PHASES = [
  {
    phase: 1,
    thresholdGBP: 50_000,
    startDate: '2026-04-06',
    label: 'Phase 1 — over £50,000',
    description: 'Self-employed individuals and landlords with qualifying income over £50,000 in 2024/25.',
    source: 'https://www.gov.uk/guidance/find-out-if-and-when-you-need-to-use-making-tax-digital-for-income-tax',
    sourceAlias: 'HMRC: Find out if and when you need to use MTD for IT',
    accessed: '2026-09-04',
  },
  {
    phase: 2,
    thresholdGBP: 30_000,
    startDate: '2027-04-06',
    label: 'Phase 2 — over £30,000',
    description: 'Threshold drops to £30,000.',
    source: 'https://www.gov.uk/guidance/find-out-if-and-when-you-need-to-use-making-tax-digital-for-income-tax',
    sourceAlias: 'HMRC: Find out if and when you need to use MTD for IT',
    accessed: '2026-09-04',
  },
  {
    phase: 3,
    thresholdGBP: 20_000,
    startDate: '2028-04-06',
    label: 'Phase 3 — over £20,000',
    description: 'Threshold drops to £20,000.',
    source: 'https://www.gov.uk/government/publications/making-tax-digital-for-income-tax-self-assessment-reducing-the-mandation-threshold-from-30000-to-20000-from-april-2028',
    sourceAlias: 'HMRC: Reducing the mandation threshold to £20,000',
    accessed: '2026-09-04',
  },
];

export const QUALIFYING_INCOME = {
  summary:
    'Qualifying income is total income from self-employment and property before expenses. PAYE employment income is excluded.',
  source: 'https://www.gov.uk/guidance/work-out-your-qualifying-income-for-making-tax-digital-for-income-tax',
  sourceAlias: 'HMRC: Work out your qualifying income for MTD for IT',
  accessed: '2026-09-04',
};

// ─── Quarterly update deadlines (2026/27 tax year) ───────────────────────────
export const QUARTERLY_DEADLINES_SOURCE = {
  source: 'https://www.gov.uk/guidance/use-making-tax-digital-for-income-tax/send-quarterly-updates',
  sourceAlias: 'HMRC: Send quarterly updates',
  accessed: '2026-09-04',
};

export const QUARTERLY_DEADLINES_2026_27 = [
  { quarter: 'Q1', periodEnd: '2026-07-05', deadline: '2026-08-07', label: '7 August 2026' },
  { quarter: 'Q2', periodEnd: '2026-10-05', deadline: '2026-11-07', label: '7 November 2026' },
  { quarter: 'Q3', periodEnd: '2027-01-05', deadline: '2027-02-07', label: '7 February 2027' },
  { quarter: 'Q4', periodEnd: '2027-04-05', deadline: '2027-05-07', label: '7 May 2027' },
];

/** Format an ISO date (YYYY-MM-DD) as a UK long date, e.g. 7 November 2026. */
export function formatUkLongDate(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${day} ${months[month - 1]} ${year}`;
}

/** Return the next upcoming quarterly deadline, or null if all have passed. */
export function getNextQuarterlyDeadline(now = new Date()) {
  for (const q of QUARTERLY_DEADLINES_2026_27) {
    if (new Date(`${q.deadline}T23:59:59Z`) > now) return q;
  }
  return null;
}

/**
 * Map qualifying income (gross trading + property) to the MTD mandation phase.
 * Thresholds are exclusive ("over £X"), matching HMRC's published bands.
 */
export function getMandationForQualifyingIncome(incomeGBP, now = new Date()) {
  const phasesHighToLow = [...MTD_PHASES].sort((a, b) => b.thresholdGBP - a.thresholdGBP);
  for (const phase of phasesHighToLow) {
    if (incomeGBP > phase.thresholdGBP) {
      const start = new Date(`${phase.startDate}T00:00:00Z`);
      return { phase, alreadyStarted: start <= now };
    }
  }
  return { phase: null, alreadyStarted: false };
}

// ─── Penalty easement ────────────────────────────────────────────────────────
export const PENALTY_EASEMENT = {
  yearOne:
    'There are no penalty points for late quarterly updates for the 2026/27 tax year.',
  laterYears:
    'For quarterly updates for later tax years, a missed deadline gives one penalty point; ' +
    'the threshold is four points, resulting in a £200 penalty, followed by a further £200 penalty ' +
    'for each subsequent missed submission deadline while the threshold is met.',
  firstLaterYearsDeadline: '2027-08-07',
  get description() {
    return `${this.yearOne} ${this.laterYears}`;
  },
  source: 'https://www.gov.uk/guidance/penalties-for-making-tax-digital-for-income-tax',
  sourceAlias: 'HMRC: Penalties for MTD for IT',
  accessed: '2026-09-04',
};

// ─── Digital links (correct instrument) ──────────────────────────────────────
export const DIGITAL_LINKS = {
  summary:
    'An MTD digital record must be created and stored using software that works with MTD for Income Tax. ' +
    'Income and expenses can be entered manually into compatible software. ' +
    'Where records move between software products, permitted digital links include CSV import/export, ' +
    'API or automated transfer, and emailing a spreadsheet containing digital records for import into another software product. ' +
    'Once a digital record has been included in a quarterly update sent to HMRC, it must not subsequently ' +
    'be manually re-keyed, copied or pasted between record-keeping systems.',
  photoNote:
    'A photograph or scan of a receipt held in WhatsApp or another source-document system is not, by itself, the MTD digital record. ' +
    'Compatible software must create and store the required digital record; receipt scanning may be used by compatible software to create that record. ' +
    'A CSV transfer can form a digital link where the data is actually exported and imported digitally between the relevant software products.',
  source: 'https://www.gov.uk/guidance/use-making-tax-digital-for-income-tax/create-digital-records',
  sourceAlias: 'HMRC: Create digital records (MTD for IT)',
  accessed: '2026-09-04',
  softwareChooser:
    'https://www.gov.uk/guidance/choose-the-right-software-for-making-tax-digital-for-income-tax',
  note: 'VAT Notice 700/22 governs MTD for VAT, not MTD for Income Tax. Do not cite it as the authority for ITSA digital link requirements.',
};

// ─── HMRC record-keeping duration ────────────────────────────────────────────
export const RECORD_KEEPING = {
  duration: 'at least 5 years after the 31 January submission deadline of the relevant tax year',
  lateReturnsNote: 'Different rules can apply to very late returns.',
  source: 'https://www.gov.uk/self-employed-records/how-long-to-keep-your-records',
  sourceAlias: 'HMRC: How long to keep your records',
  accessed: '2026-09-04',
};

// ─── Positioning copy (locked) ───────────────────────────────────────────────
export const POSITIONING = {
  hero: 'Send receipts by WhatsApp. Get structured records ready for your accountant.',
  subHero:
    'ZapExpenses captures source documents and extracts information for you to review and export ' +
    'for use alongside MTD-compatible software.',
  noSubmit:
    'ZapExpenses does not itself send quarterly updates or tax returns to HMRC and does not, on its own, ' +
    'satisfy the MTD for Income Tax submission requirement.',
  notTaxAdvice:
    'Suggested categories, VAT treatment and extracted values should be reviewed before use. ' +
    'ZapExpenses does not provide tax advice.',
  retention:
    'Records are kept while your account is active so you can export them at any time. ' +
    'HMRC requires self-employed business records to be kept for at least 5 years after the 31 January ' +
    'submission deadline for the relevant tax year, although different rules can apply to very late returns. ' +
    'ZapExpenses\' own account-retention and deletion policy is separate from HMRC\'s requirements, ' +
    'and users remain responsible for retaining the records and supporting evidence they are required to keep. ' +
    'You may request deletion of your data at any time by contacting hello@zapexpenses.com.',
  clientExperience:
    'No app to download, no login to remember, no dashboard to learn — that is the client capture experience. ' +
    'Accountants and power users can still review extracted records on the web.',
  reports:
    'Structured CSV plus receipt archive, ready for import into your accounting or filing software.',
};
